import { AxiosResponse } from 'axios';
import {
  SET_CUSTOM_FONT,
  SET_CUSTOM_FONTS,
  SET_ERRORMSG,
} from '@/store/mutation_types';
import {
  CLEAR_CUSTOM_FONT_ISLOADING,
  DELETE_CUSTOM_FONT,
  EDIT_CUSTOM_FONT,
  GET_CUSTOM_FONTS,
  REGISTER_CUSTOM_FONT,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import CustomFont from '@/services/custom_font';
import { Module } from 'vuex';
import { EditCustomFont, GetCustomFont } from '@/dtos/custom_font.dto';
import { applyCustomFonts } from '@/utils/custom_fonts';

/* -------------------------------------------------------------------------
   Custom fonts an organization has uploaded. `customFonts.data` is the list
   the Settings › Fonts panel renders and that `@/utils/custom_fonts` registers
   with the browser, so every font picker in the app can offer them.

   `GET_CUSTOM_FONTS` resolves to the array rather than a boolean (the way
   GET_ZMANIM_DISPLAY_OVERRIDES_N does) so a caller that only needs the list
   does not have to reach back into the store for it.
   ------------------------------------------------------------------------- */
const customFont: Module<any, {}> = {
  state: {
    customFont: {
      data: null,
      isLoading: false,
    },
    customFonts: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    customFont: (state: { customFont: any }) => state.customFont,
    customFonts: (state: { customFonts: any }) => state.customFonts,
  },
  mutations: {
    [SET_CUSTOM_FONT]: (
      state: { customFont: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.customFont.data = payload.data) : null,
        (state.customFont.isLoading = payload.isLoading);
    },
    [SET_CUSTOM_FONTS]: (
      state: { customFonts: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.customFonts.data = payload.data) : null,
        (state.customFonts.isLoading = payload.isLoading);
    },
  },
  actions: {
    [REGISTER_CUSTOM_FONT]: async ({ commit }: any, payload: FormData) => {
      commit(SET_CUSTOM_FONT, { data: null, isLoading: true });

      const result: AxiosResponse = await CustomFont.registerCustomFont(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_CUSTOM_FONT, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_CUSTOM_FONT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return null;
      } else {
        commit(SET_CUSTOM_FONT, { isLoading: false });
        return null;
      }
    },
    [GET_CUSTOM_FONTS]: async ({ commit }: any, payload: any) => {
      commit(SET_CUSTOM_FONTS, { data: null, isLoading: true });

      const result: AxiosResponse = await CustomFont.getCustomFonts(
        payload as GetCustomFont
      );

      if (result) {
        if (result.data.status) {
          const responseData = result.data.data;
          const records = Array.isArray(responseData)
            ? responseData
            : responseData?.data || [];
          commit(SET_CUSTOM_FONTS, { data: records, isLoading: false });
          // Hand the faces to the browser and refresh the Quill rules here, so
          // every consumer of the list — the Settings panel, the font pickers,
          // the Display — renders in the real font without having to remember.
          applyCustomFonts(records);
          return records;
        }
        commit(SET_CUSTOM_FONTS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return [];
      } else {
        commit(SET_CUSTOM_FONTS, { isLoading: false });
        return [];
      }
    },
    [EDIT_CUSTOM_FONT]: async ({ commit }: any, payload: EditCustomFont) => {
      commit(SET_CUSTOM_FONT, { data: null, isLoading: true });

      const result: AxiosResponse = await CustomFont.editCustomFont(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_CUSTOM_FONT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_CUSTOM_FONT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_CUSTOM_FONT, { isLoading: false });
        return false;
      }
    },
    [DELETE_CUSTOM_FONT]: async ({ commit }: any, payload: string) => {
      commit(SET_CUSTOM_FONT, { data: null, isLoading: true });

      const result: AxiosResponse = await CustomFont.deleteCustomFont(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_CUSTOM_FONT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_CUSTOM_FONT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_CUSTOM_FONT, { isLoading: false });
        return false;
      }
    },
    [CLEAR_CUSTOM_FONT_ISLOADING]: ({ commit }) => {
      commit(SET_CUSTOM_FONT, { isLoading: false });
      commit(SET_CUSTOM_FONTS, { isLoading: false });
    },
  },
};

export default customFont;
